import { Controller } from '@hotwired/stimulus';

// Opens the Jerico Insight modal, POSTs to the create endpoint, animates
// the stage labels independently of the server, and observes the body's
// data-state attribute so the timer stops as soon as the server-driven
// Turbo morph swaps the body away from `stages` (per spec §8.7).
export default class extends Controller {
  static values = { invoiceDocumentId: Number, modalId: String };
  static targets = ['stage'];

  connect() {
    this.#startBodyObserver();
  }

  disconnect() {
    this.#stopBodyObserver();
    this.#stopStageTimer();
  }

  open() {
    // Delegate to the modal controller (auto-mounted on the same element by
    // `Ui::ModalComponent`'s `merged_data_attrs`) so the lifecycle stays
    // consistent: `openModalStack` push for correct Escape-key routing when
    // the Jerico Insight modal sits on top of the invoice-preview modal,
    // `body { overflow: hidden }` scroll-lock, and the enter transition on
    // `contentTarget`. `modalController` is assigned in `modal_controller#
    // connect()` at DOM-parse time, so it is always present by the time a
    // user click reaches us — the optional-chain is purely defensive.
    document.getElementById(this.modalIdValue)?.modalController?.open();
    // Stage-timer start lives in the body MutationObserver (see
    // `#startBodyObserver`) — the trigger and the modal each get their
    // own Stimulus instance, but stage targets only exist inside the
    // modal's scope. Calling `#startStageTimer()` from the trigger
    // instance was a no-op (empty `stageTargets`) and only added noise
    // for future readers.
    this.#postCreate();
  }

  close() {
    document.getElementById(this.modalIdValue)?.modalController?.close();
    this.#stopStageTimer();
  }

  // CTA bridge — close the Insight modal and ask the parent invoice preview
  // modal to enter annotation mode. Cross-controller contract via a
  // `jerico-insight:show-annotations` window CustomEvent — see
  // `annotation_mode_controller.js` for the listener side. Decoupled: if no
  // listener is wired, the Insight modal still closes.
  openOnFacture() {
    this.close();
    window.dispatchEvent(
      new CustomEvent('jerico-insight:show-annotations', {
        detail: { page: 1 },
      })
    );
  }

  async #postCreate() {
    try {
      const res = await fetch(`/invoice_documents/${this.invoiceDocumentIdValue}/jerico_insight`, {
        method: 'POST',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': this.#csrf(),
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        console.warn('[jerico-insight] POST failed:', res.status);
        return;
      }
      const html = await res.text();
      window.Turbo?.renderStreamMessage?.(html);
    } catch (e) {
      console.warn('[jerico-insight] network error', e);
    }
  }

  #startStageTimer() {
    // Idempotent: the body MutationObserver re-fires on every stage
    // `data-state` mutation (since it observes the modal subtree). Without
    // this guard, each fire would clear the in-flight interval and restart
    // at i=0 — freezing the visual at "stage 1 done, stage 2 active" forever.
    // We only ever stop the timer when the body state leaves `stages` (see
    // `reactToBodyState` in `#startBodyObserver`).
    if (this.stageTimer) return;
    if (this.stageTargets.length === 0) return;
    let i = 0;
    this.#updateMarker(this.stageTargets[0]);
    this.stageTimer = setInterval(() => {
      if (i >= this.stageTargets.length - 1) {
        // Final stage stays `active` (spinner keeps spinning) until the
        // server-driven morph replaces the body with findings/empty/error.
        clearInterval(this.stageTimer);
        this.stageTimer = null;
        return;
      }
      this.stageTargets[i].dataset.state = 'done';
      this.stageTargets[i + 1].dataset.state = 'active';
      this.#updateMarker(this.stageTargets[i]);
      this.#updateMarker(this.stageTargets[i + 1]);
      i += 1;
    }, 2500);
  }

  #stopStageTimer() {
    if (this.stageTimer) {
      clearInterval(this.stageTimer);
      this.stageTimer = null;
    }
  }

  #updateMarker(stageEl) {
    const state = stageEl.dataset.state;
    const spinner = stageEl.querySelector('.stage-spinner');
    const check = stageEl.querySelector('.stage-check');
    const skeleton = stageEl.querySelector('.stage-skeleton');
    const marker = stageEl.querySelector('.stage-marker');
    spinner?.classList.toggle('hidden', state !== 'active');
    check?.classList.toggle('hidden', state !== 'done');
    skeleton?.classList.toggle('hidden', state !== 'pending');
    if (state === 'done') {
      marker?.classList.remove(
        'bg-neutral-100',
        'text-neutral-400',
        'bg-primary-100',
        'text-primary-700'
      );
      marker?.classList.add('bg-success-100', 'text-success-700');
    } else if (state === 'active') {
      marker?.classList.remove(
        'bg-neutral-100',
        'text-neutral-400',
        'bg-success-100',
        'text-success-700'
      );
      marker?.classList.add('bg-primary-100', 'text-primary-700');
    }
  }

  #startBodyObserver() {
    // Observe the MODAL element (stable) rather than the body div, because
    // `turbo_stream.replace` swaps the body div for a brand-new node — an
    // observer attached to the old div would never fire for the new one.
    // Watching the modal subtree with childList + attributeFilter catches
    // both: (a) initial idle → stages replace, and (b) stages → findings
    // morph from the broadcast on cross-check completion.
    //
    // NOTE: stage `data-state` mutations (`pending` → `active` → `done`)
    // sit inside the subtree and ALSO trigger this observer. The reaction
    // below is safe to re-enter because `#startStageTimer` is idempotent
    // — if a timer is already in flight, the call returns immediately
    // without resetting `i`. The timer only ever stops when the body
    // state leaves `stages` (broadcast morph to findings/empty/error).
    const reactToBodyState = () => {
      const body = document.getElementById(`jerico_insight_body_${this.invoiceDocumentIdValue}`);
      if (!body) return;
      if (body.dataset.state === 'stages') {
        this.#startStageTimer();
      } else {
        this.#stopStageTimer();
      }
    };
    // Pick up an in-flight state on initial mount (modal re-rendered with
    // a pre-existing cross_check in pending/processing).
    reactToBodyState();
    this.bodyObserver = new MutationObserver(reactToBodyState);
    this.bodyObserver.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });
  }

  #stopBodyObserver() {
    this.bodyObserver?.disconnect();
    this.bodyObserver = null;
  }

  #csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
